import { useCallback, useContext, useMemo } from 'react';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { UserContext } from '@namespace/user';
import { MIN_GAMES_SEARCH_QUERY_LENGTH } from '../../constants';
import { CasinoTypeUnion, IGame } from '../../types';

type UseCasinoSearchLogAction = (
  searchQuery: string,
  casinoType: CasinoTypeUnion,
  numberOfFoundGames: number,
  filterState: {
    categories: string[];
    providers: string[];
  }
) => {
  handleGoBackClick: () => void;
  handleLaunchGameClick: (selectedGame: IGame) => void;
};

export const useCasinoSearchLogAction: UseCasinoSearchLogAction = (
  searchQuery,
  casinoType,
  numberOfFoundGames,
  filterState
) => {
  const [user] = useContext(UserContext);

  const { id: userId = 0 } = user;

  const isAnyFilterSearchFilled =
    filterState.providers.length ||
    filterState.categories.length ||
    searchQuery.length >= MIN_GAMES_SEARCH_QUERY_LENGTH;

  const filtersApplied = useMemo(
    () => ({
      providersApplied: filterState.providers.length
        ? filterState.providers
        : 0,
      categoriesApplied: filterState.categories.length
        ? filterState.categories
        : 0
    }),
    [filterState]
  );

  const handleLaunchGameClick = useCallback(
    (selectedGame: IGame) => {
      if (isAnyFilterSearchFilled) {
        logAction(LOG_ACTIONS.CASINO_SEARCH_LAUNCH_FOUND_GAME, {
          userId,
          searchQuery:
            searchQuery.length >= MIN_GAMES_SEARCH_QUERY_LENGTH
              ? searchQuery
              : 0,
          casinoType,
          selectedGame: selectedGame.gameName,
          ...filtersApplied
        });
      }
    },
    [userId, searchQuery, casinoType, isAnyFilterSearchFilled, filtersApplied]
  );

  const handleGoBackClick = useCallback(() => {
    if (isAnyFilterSearchFilled) {
      logAction(LOG_ACTIONS.CASINO_SEARCH_LEAVE_SEARCH_PAGE, {
        userId,
        searchQuery:
          searchQuery.length >= MIN_GAMES_SEARCH_QUERY_LENGTH ? searchQuery : 0,
        casinoType,
        numberOfFoundGames,
        ...filtersApplied
      });
    }
  }, [
    userId,
    searchQuery,
    numberOfFoundGames,
    casinoType,
    isAnyFilterSearchFilled,
    filtersApplied
  ]);

  return { handleGoBackClick, handleLaunchGameClick };
};
